import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { buildAiTopicKeywordListId } from "app/modules/demand-analysis/keyword-lists";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import { allTrackers } from "app/services/track/track";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IDemandAnalysisHomePageParams } from "../../../types";
import type { Topic } from "../../../api-client/demandSearchTrendsTopicsAPI";
import { useTopicsData } from "../../../api-client/demandSearchTrendsTopicsAPI";

import { TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItems as TopicGroupSubItems } from "./TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItems";
import { TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItemTooltip as GroupItemTooltip } from "./TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItemTooltip";
import type { FlattenedTopic } from "./types";

const MAX_VISIBLE_ITEMS = 6;

const TopicGroupContainer = styled.div`
    min-width: 0;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    border-radius: 6px;
    background: ${colorsPalettes.blue[50]};

    span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-transform: capitalize;
    }
`;

const ShowMoreButtonContainer = styled.div`
    display: flex;
    width: 70%;
`;

const flattenSubtopics = (topic: Topic): FlattenedTopic[] => {
    const result: FlattenedTopic[] = [];

    if (!topic.Children) {
        return result;
    }

    const processSubtopics = (currentTopic: Topic, parentId: string | null) => {
        if (!currentTopic.Children) {
            return;
        }

        Object.values(currentTopic.Children).forEach((subtopic) => {
            result.push({ item: subtopic, parentId });

            processSubtopics(subtopic, subtopic.Id);
        });
    };

    processSubtopics(topic, null);

    return result;
};

type TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupProps = {
    topic: Topic;
    placement: "left" | "right";
};

export const TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroup = ({
    topic,
    placement,
}: TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupProps) => {
    const t = useTranslation();
    const { data } = useTopicsData();
    const { params } = useCurrentStateAndParams<IDemandAnalysisHomePageParams>();
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(false);
    }, [topic]);

    const allTitles = useMemo(() => flattenSubtopics(topic), [topic]);
    // Total count includes the main topic
    const totalTitles = allTitles.length + 1;

    // Limit to first 6 items (plus main topic = 7) if not expanded
    const visibleItems = expanded ? allTitles : allTitles.slice(0, MAX_VISIBLE_ITEMS);
    const hiddenCount = totalTitles - visibleItems.length - 1;

    const toggleExpand = () => {
        setExpanded(!expanded);
    };

    const handleItemClick = (id: string) => {
        const selectedTopic = data?.topicsById.get(id);

        if (!selectedTopic) {
            return;
        }

        const showClusters = "showClusters" in params ? params.showClusters : undefined;
        allTrackers.dispatchTrackEvent("Select keyword list", "click", "Tree", {
            target_KW_list_type: "AiTopic",
            target_KW_list_name: selectedTopic.Name,
            target_Clusters: showClusters === "true" ? "on" : "off",
        });

        try {
            const navigatorParams = getDefaultParamsForState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE);
            const keywordListId = buildAiTopicKeywordListId(selectedTopic.Name);

            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
                ...navigatorParams,
                id: [keywordListId],
                country: params.country,
            });
        } catch (error) {
            Sentry.error(
                "Error navigating from demand analysis home page to AI topic analysis page after topic selection: ",
                error as Error,
            );
        }
    };

    return (
        <TopicGroupContainer>
            <GroupItemTooltip
                item={topic}
                key={topic.Path}
                placement={placement}
            >
                <Text
                    padding="8px 16px"
                    fontVariant="subTitle3"
                    color={colorsPalettes.carbon[500]}
                    borderRadius="6px"
                    cursor="pointer"
                    whenHover={{
                        background: rgba(colorsPalettes.blue[400], 0.1),
                        color: colorsPalettes.blue[500],
                    }}
                    onClick={() => handleItemClick(topic.Id)}
                >
                    {topic.Name}
                </Text>
            </GroupItemTooltip>
            <TopicGroupSubItems
                items={visibleItems}
                parentTopic={topic}
                placement={placement}
                onItemClick={handleItemClick}
            />
            <ShowMoreButtonContainer>
                {hiddenCount > 0 ? (
                    <IconButton
                        type="flat"
                        iconSize="xs"
                        placement="right"
                        iconName="chev-down"
                        onClick={toggleExpand}
                    >
                        {t("marketresearch.keywordmarket.home.topics.cube.show.more", {
                            number: hiddenCount,
                        })}
                    </IconButton>
                ) : null}
                {expanded ? (
                    <IconButton
                        type="flat"
                        iconSize="xs"
                        placement="right"
                        iconName="chev-up"
                        onClick={toggleExpand}
                    >
                        {t("marketresearch.keywordmarket.home.topics.cube.show.less")}
                    </IconButton>
                ) : null}
            </ShowMoreButtonContainer>
        </TopicGroupContainer>
    );
};
